import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { loadStoreSettings } from "../../../util/storeSettings";
import { brandLogo } from "../../../assets/img/logo/brandLogos";
import moment from "moment";
import {
  FaArrowLeft,
  FaPrint,
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaHistory,
  FaUser,
  FaBoxOpen,
  FaTruck,
  FaMoneyBillWave,
  FaCreditCard,
  FaUndoAlt,
  FaCalendarAlt,
  FaShieldAlt,
  FaExternalLinkAlt
} from "react-icons/fa";

import { imageurl } from "../../../App";

export default function InvoiceDetails(props) {
  const params = useParams();
  const orderId = props.match?.params?.order_id || params?.order_id;
  const [loading, setLoading] = useState(true);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [customerStats, setCustomerStats] = useState({ total_orders: 1, total_spent: 0 });
  const [activeTab, setActiveTab] = useState("all"); // 'all', 'invoice', 'history'
  const [storeSettings, setStoreSettings] = useState(null);

  useEffect(() => {
    document.title = `Tax Invoice #${orderId} — Admin`;
    
    // Fetch store configuration for branding, currency & invoice header
    loadStoreSettings().then((settings) => {
      if (settings && (settings.storeName || settings.logoUrl)) {
        setStoreSettings(settings);
      }
    }).catch(() => {});

    axios
      .get(`/api/fetch-invoice-details/${orderId}`)
      .then((res) => {
        if (res.data.status === 200 && res.data.data) {
          setInvoiceDetails(res.data.data.orderDetails || []);
          setTotal(res.data.data.total_products_cost || 0);
          setOrder(res.data.data.order || []);
          setTimeline(res.data.data.timeline || []);
          setCustomerOrders(res.data.data.customer_order_history || []);
          if (res.data.data.customer_stats) {
            setCustomerStats(res.data.data.customer_stats);
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [orderId]);

  if (loading) {
    return (
      <div className="py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted mt-2 fw-semibold">Loading Sales Invoice #{orderId} & Order History...</p>
      </div>
    );
  }

  const orderInfo = order[0] || {};
  const isFullyRefunded = orderInfo.order_status === "Refunded" || Number(orderInfo.status) === 5;
  const isPartiallyRefunded = orderInfo.order_status === "Partially Refunded" || (!isFullyRefunded && Number(orderInfo.order_refund) === 1);
  const isRefunded = isFullyRefunded;
  const isCod = (orderInfo.payment_mode || "").toUpperCase() === "COD";

  const currencySymbol = storeSettings?.currencySymbol || "Rs.";
  const storeName = storeSettings?.storeName || "Gootocart";
  const supportEmail = storeSettings?.supportEmail || "";
  const warehouseCity = storeSettings?.warehouseCity || "Karachi";
  const country = storeSettings?.country || "Pakistan";
  const storeLogo = storeSettings?.logoUrl
    ? (storeSettings.logoUrl.startsWith("http")
        ? storeSettings.logoUrl
        : `${imageurl}/${storeSettings.logoUrl.replace(/^\/+/, "")}`)
    : brandLogo;

  const formatOrderDate = (date1, date2, formatStr = "DD MMMM YYYY") => {
    if (date1 && typeof date1 === "string" && !date1.startsWith("0000") && moment(date1).isValid()) {
      return moment(date1).format(formatStr);
    }
    if (date2 && typeof date2 === "string" && !date2.startsWith("0000") && moment(date2).isValid()) {
      return moment(date2).format(formatStr);
    }
    return moment().format(formatStr);
  };

  const formatTimelineDate = (dateVal, formatStr = "DD MMM, hh:mm A") => {
    if (dateVal && typeof dateVal === "string" && !dateVal.startsWith("0000") && moment(dateVal).isValid()) {
      return moment(dateVal).format(formatStr);
    }
    return "—";
  };

  return (
    <div className="container-fluid px-0">
      {/* Top Header and Actions (Hidden when printing invoice) */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4 d-print-none">
        <div className="d-flex align-items-center gap-2">
          <Link to="/admin/invoice" className="btn btn-sm btn-light border rounded-pill px-3 shadow-sm">
            <FaArrowLeft className="me-1" /> Back to Show Sales
          </Link>
          <span className="text-muted">|</span>
          <h5 className="fw-bold mb-0 text-dark">
            Invoice #{orderId}
          </h5>
          {isFullyRefunded ? (
            <span className="badge rounded-pill bg-danger bg-opacity-10 text-danger border border-danger px-2.5 py-1">
              <FaUndoAlt className="me-1" /> Fully Refunded
            </span>
          ) : isPartiallyRefunded ? (
            <span className="badge rounded-pill bg-warning bg-opacity-10 text-warning border border-warning px-2.5 py-1">
              <FaUndoAlt className="me-1" /> Partially Refunded
            </span>
          ) : (
            <span className="badge rounded-pill bg-success bg-opacity-10 text-success border border-success px-2.5 py-1">
              <FaCheckCircle className="me-1" /> Completed Sale
            </span>
          )}
        </div>

        <div className="d-flex align-items-center gap-2">
          {/* Quick tab switcher */}
          <div className="btn-group btn-group-sm me-2">
            <button
              onClick={() => setActiveTab("all")}
              className={`btn ${activeTab === "all" ? "btn-dark" : "btn-light border"}`}
            >
              Full View
            </button>
            <button
              onClick={() => setActiveTab("invoice")}
              className={`btn ${activeTab === "invoice" ? "btn-dark" : "btn-light border"}`}
            >
              Invoice Slip
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`btn ${activeTab === "history" ? "btn-dark" : "btn-light border"}`}
            >
              Order History ({timeline.length})
            </button>
          </div>

          <Link
            to={`/admin/orders/${orderId}`}
            className="btn btn-sm btn-outline-dark rounded-pill px-3 fw-semibold d-flex align-items-center gap-1 shadow-sm"
          >
            <FaBoxOpen size={12} /> Order Management
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="btn btn-sm btn-primary rounded-pill px-3.5 py-1.5 fw-bold d-flex align-items-center gap-2 shadow-sm"
          >
            <FaPrint size={13} /> Print Tax Invoice
          </button>
        </div>
      </div>

      <div className="row g-4">
        {/* Main Invoice Slip */}
        {(activeTab === "all" || activeTab === "invoice") && (
          <div className={activeTab === "all" ? "col-lg-8" : "col-12"}>
            <div className="admin-card p-4 p-md-5 bg-white shadow-sm border mb-4">
              {/* Document Header */}
              <div className="d-flex justify-content-between align-items-start border-bottom pb-4 mb-4">
                <div>
                  <img
                    src={storeLogo}
                    alt={storeName}
                    style={{ maxHeight: "45px", maxWidth: "180px", objectFit: "contain" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = brandLogo;
                    }}
                  />
                  <p className="text-muted mb-0 mt-2" style={{ fontSize: "13px", lineHeight: 1.5 }}>
                    <strong className="text-dark d-block">{storeName}</strong>
                    {storeSettings?.warehouseAddress ? `${storeSettings.warehouseAddress}, ` : ""}
                    {warehouseCity}, {country} • {supportEmail}
                  </p>
                </div>

                <div className="text-end">
                  <h3 className="fw-extrabold text-primary mb-1">INVOICE</h3>
                  <span className="fw-bold d-block text-dark" style={{ fontSize: "14px" }}>
                    #INV-{orderId}
                  </span>
                  <code className="text-muted" style={{ fontSize: "12px" }}>
                    Tracking: {orderInfo.tracking_no}
                  </code>
                  <small className="d-block text-muted mt-1">
                    Date: {formatOrderDate(orderInfo.order_complete, orderInfo.created_at)}
                  </small>
                </div>
              </div>

              {/* Customer & Billing Info */}
              <div className="row g-4 mb-4 pb-3 border-bottom" style={{ fontSize: "13.5px" }}>
                <div className="col-6">
                  <span className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: "11px" }}>
                    BILLED / SHIPPED TO:
                  </span>
                  <h6 className="fw-bold mb-1 text-dark">
                    {orderInfo.firstname} {orderInfo.lastname}
                  </h6>
                  <p className="text-muted mb-0">
                    {orderInfo.address}<br />
                    {orderInfo.city}, Pakistan<br />
                    Phone: {orderInfo.phone}<br />
                    Email: {orderInfo.email}
                  </p>
                </div>

                <div className="col-6 text-end">
                  <span className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: "11px" }}>
                    PAYMENT & FULFILLMENT:
                  </span>
                  <span className={`badge ${isRefunded ? "bg-danger-subtle text-danger" : "bg-success-subtle text-success"} border px-2 py-1 mb-1`}>
                    <FaCheckCircle className="me-1" /> {isRefunded ? "Order Refunded" : "Payment Verified"}
                  </span>
                  <p className="text-muted mb-0">
                    Method: {isCod ? "Cash on Delivery" : orderInfo.payment_mode}<br />
                    Status: {orderInfo.order_status || (isRefunded ? "Refunded" : "Completed & Delivered")}
                  </p>
                </div>
              </div>

              {/* Itemized Table */}
              <div className="table-responsive mb-4">
                <table className="table table-bordered align-middle mb-0">
                  <thead className="table-light text-uppercase" style={{ fontSize: "12px" }}>
                    <tr>
                      <th style={{ width: "40px" }}>#</th>
                      <th>Item Description</th>
                      <th className="text-center">Rate</th>
                      <th className="text-center">Qty</th>
                      <th className="text-center">Discount</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontSize: "13.5px" }}>
                    {invoiceDetails.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-muted">{idx + 1}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold text-dark">{item.product_name}</span>
                            {Number(item.refund) === 1 && (
                              <span className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle px-1.5 py-0.5" style={{ fontSize: "10.5px" }}>
                                Refunded
                              </span>
                            )}
                          </div>
                          {item.sku ? (
                            <small className="d-block text-muted">Item code: {item.sku}</small>
                          ) : item.product_id ? (
                            <small className="d-block text-muted">SKU/ID: #{item.product_id}</small>
                          ) : null}
                        </td>
                        <td className="text-center font-monospace">{currencySymbol} {Number(item.product_price).toLocaleString()}</td>
                        <td className="text-center">{item.product_qty}</td>
                        <td className="text-center text-success font-monospace">
                          {Number(item.discount_given || 0) > 0 ? `-${currencySymbol} ${Number(item.discount_given).toLocaleString()}` : "—"}
                        </td>
                        <td className="text-end fw-bold text-dark font-monospace">
                          {currencySymbol} {Number(item.total_price).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Financial Summary */}
              {(() => {
                const originalItemsSubtotal = invoiceDetails.reduce(
                  (sum, item) => sum + Number(item.product_price || 0) * Number(item.product_qty || 1),
                  0
                );
                const totalCatalogDiscount = invoiceDetails.reduce(
                  (sum, item) => sum + Number(item.discount_given || 0),
                  0
                );
                const couponDisc = Number(orderInfo.coupon_discount || 0);
                const totalSavings = totalCatalogDiscount + couponDisc;
                const paidSubtotal = Number(total || (originalItemsSubtotal - totalCatalogDiscount));

                return (
                  <div className="d-flex justify-content-end mb-4">
                    <div style={{ width: "360px", fontSize: "13.5px" }}>
                      <div className="d-flex justify-content-between py-1 border-bottom">
                        <span className="text-muted">Items Subtotal (Real Price):</span>
                        <span className="fw-semibold text-dark font-monospace">
                          {currencySymbol} {originalItemsSubtotal.toLocaleString()}
                        </span>
                      </div>

                      {totalCatalogDiscount > 0 && (
                        <div className="d-flex justify-content-between py-1 border-bottom text-success">
                          <span>Catalog Discount Savings:</span>
                          <span className="fw-bold font-monospace">
                            -{currencySymbol} {totalCatalogDiscount.toLocaleString()}
                          </span>
                        </div>
                      )}

                      {totalCatalogDiscount > 0 && (
                        <div className="d-flex justify-content-between py-1 border-bottom bg-light px-2 rounded">
                          <span className="fw-semibold text-dark">Items Subtotal (Discounted):</span>
                          <span className="fw-bold text-dark font-monospace">
                            {currencySymbol} {paidSubtotal.toLocaleString()}
                          </span>
                        </div>
                      )}

                      {couponDisc > 0 && (
                        <div className="d-flex justify-content-between py-1 border-bottom text-danger">
                          <span>Coupon Discount {orderInfo.coupon_code ? `(${orderInfo.coupon_code})` : ""}:</span>
                          <span className="fw-bold font-monospace">-{currencySymbol} {couponDisc.toLocaleString()}</span>
                        </div>
                      )}

                      <div className="d-flex justify-content-between py-1 border-bottom">
                        <span className="text-muted">Nationwide Shipping:</span>
                        <span className={Number(orderInfo.shipping_fee || 0) === 0 ? "fw-bold text-success font-monospace" : "fw-bold text-dark font-monospace"}>
                          {Number(orderInfo.shipping_fee || 0) === 0 ? "FREE" : `${currencySymbol} ${Number(orderInfo.shipping_fee).toLocaleString()}`}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between py-2 align-items-baseline">
                        <span className="fw-bold fs-6">Grand Total:</span>
                        <span className="fw-extrabold fs-5 text-primary font-monospace">
                          {currencySymbol} {Math.max(0, paidSubtotal + Number(orderInfo.shipping_fee || 0) - couponDisc).toLocaleString()}
                        </span>
                      </div>

                      {totalSavings > 0 && (
                        <div className="d-flex justify-content-between pt-1 text-success fw-bold" style={{ fontSize: "12px" }}>
                          <span>Total Customer Savings:</span>
                          <span className="font-monospace">{currencySymbol} {totalSavings.toLocaleString()} OFF</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Footer Note */}
              <div className="border-top pt-3 text-center text-muted" style={{ fontSize: "12px" }}>
                Thank you for choosing {storeName}!{supportEmail ? ` For any inquiries, please contact ${supportEmail}.` : ""}
              </div>
            </div>
          </div>
        )}

        {/* Right / Side Column: Chronological Order History & Timeline */}
        {(activeTab === "all" || activeTab === "history") && (
          <div className={activeTab === "all" ? "col-lg-4 d-print-none" : "col-12 d-print-none"}>
            {/* Order Lifecycle Event History */}
            <div className="admin-card mb-4 shadow-sm">
              <div className="admin-card-header bg-white py-3 d-flex align-items-center justify-content-between">
                <h6 className="admin-card-title d-flex align-items-center gap-2 mb-0">
                  <FaHistory className="text-primary" /> Order History & Event Log
                </h6>
                <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary small">
                  {timeline.length} Events
                </span>
              </div>
              <div className="admin-card-body p-3">
                {timeline.length === 0 ? (
                  <div className="text-center text-muted py-4 small">
                    <FaHistory size={24} className="opacity-50 mb-2" />
                    <p className="mb-0">No timeline events recorded yet.</p>
                  </div>
                ) : (
                  <div className="position-relative ps-3" style={{ borderLeft: "2px solid #e2e8f0" }}>
                    {timeline.map((event, idx) => {
                      const isRefund = event.event_type === "REFUND_PROCESSED" || event.event_type === "ORDER_REFUNDED" || Boolean(event.metadata?.refund_no);
                      const isCompleted = event.event_type === "ORDER_COMPLETED" || event.event_type === "ORDER_DELIVERED";
                      const markerColor = isRefund ? "#ef4444" : isCompleted ? "#10b981" : "#2563eb";

                      return (
                        <div key={idx} className="mb-3 position-relative ps-2">
                          {/* Bullet Marker */}
                          <div
                            className="position-absolute rounded-circle"
                            style={{
                              width: "10px",
                              height: "10px",
                              left: "-19px",
                              top: "5px",
                              background: markerColor,
                              boxShadow: `0 0 0 3px ${markerColor}33`,
                            }}
                          />
                          <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-1">
                            <div className="d-flex align-items-center gap-1.5 flex-wrap">
                              <span className="fw-bold text-dark small">
                                {event.title || event.event_type}
                              </span>
                              {isRefund && (
                                <span className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle" style={{ fontSize: "10px" }}>
                                  <FaUndoAlt className="me-1" size={8} /> Refund
                                </span>
                              )}
                            </div>
                            <span className="text-muted" style={{ fontSize: "11px" }}>
                              {formatTimelineDate(event.created_at)}
                            </span>
                          </div>

                          {event.description && (
                            <p className="text-muted small mb-1 mt-0.5" style={{ fontSize: "12px" }}>
                              {event.description}
                            </p>
                          )}

                          {isRefund && (
                            <div className="p-2 my-1 rounded bg-light border border-danger-subtle small" style={{ fontSize: "11.5px" }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="font-monospace fw-semibold text-dark">
                                  {event.metadata?.refund_no || "REF-TRANSACTION"}
                                </span>
                                <span className="fw-bold text-danger font-monospace">
                                  Rs. {Number(event.metadata?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                              </div>
                              {event.metadata?.reason && (
                                <div className="text-muted mt-1 text-truncate">
                                  Reason: {event.metadata.reason}
                                </div>
                              )}
                              <div className="mt-1 d-flex justify-content-between align-items-center gap-1 text-muted" style={{ fontSize: "10.5px" }}>
                                <span>{event.metadata?.restock_inventory !== false ? "✓ Restocked" : "✗ No Restock"}</span>
                                <span>{event.metadata?.ledger_reconciliation ? "⚖️ Double-Entry Balanced" : ""}</span>
                              </div>
                            </div>
                          )}

                          {event.actor_name && (
                            <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                              <span className="badge bg-light text-secondary border px-1.5 py-0.5">
                                {event.actor_type || "ACTOR"}: {event.actor_name}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Customer Lifetime Orders Card */}
            <div className="admin-card mb-4 shadow-sm">
              <div className="admin-card-header bg-white py-3 d-flex align-items-center justify-content-between">
                <h6 className="admin-card-title d-flex align-items-center gap-2 mb-0">
                  <FaUser className="text-success" /> Customer Order History
                </h6>
                <span className="badge rounded-pill bg-light text-dark border small">
                  {customerStats.total_orders || customerOrders.length} Orders
                </span>
              </div>
              <div className="admin-card-body p-3">
                {/* Stats chips */}
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <div className="bg-light p-2 rounded-3 border text-center">
                      <span className="text-muted d-block small" style={{ fontSize: "11px" }}>Lifetime Orders</span>
                      <strong className="fs-6 text-dark">{customerStats.total_orders || customerOrders.length}</strong>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="bg-light p-2 rounded-3 border text-center">
                      <span className="text-muted d-block small" style={{ fontSize: "11px" }}>Total Spent</span>
                      <strong className="fs-6 text-success">
                        {currencySymbol} {Number(customerStats.total_spent || total).toLocaleString()}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* List of customer orders */}
                <h6 className="text-uppercase text-muted fw-bold mb-2" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                  Recent Orders by Customer
                </h6>
                <div className="list-group list-group-flush border-top border-bottom mb-2">
                  {customerOrders.length === 0 ? (
                    <div className="text-muted small py-2 text-center">No other orders found for this customer.</div>
                  ) : (
                    customerOrders.map((co, cIdx) => (
                      <div
                        key={cIdx}
                        className={`list-group-item px-1 py-2 d-flex align-items-center justify-content-between ${
                          Number(co.id) === Number(orderId) ? "bg-light rounded-2 fw-semibold" : ""
                        }`}
                        style={{ fontSize: "12.5px" }}
                      >
                        <div>
                          <div className="d-flex align-items-center gap-1">
                            <span className="font-monospace text-dark">#{co.tracking_no || co.id}</span>
                            {Number(co.id) === Number(orderId) && (
                              <span className="badge bg-primary text-white" style={{ fontSize: "10px" }}>Current</span>
                            )}
                          </div>
                          <span className="text-muted" style={{ fontSize: "11px" }}>
                            {formatOrderDate(co.order_complete, co.created_at, "DD MMM YYYY")} • {co.payment_mode || "COD"}
                          </span>
                        </div>
                        <div className="text-end">
                          <div className="fw-bold text-dark font-monospace">
                            {currencySymbol} {Number(co.total_amount || 0).toLocaleString()}
                          </div>
                          {Number(co.id) !== Number(orderId) && (
                            <Link
                              to={`/admin/invoice-details/${co.id}`}
                              className="text-primary text-decoration-none fw-semibold"
                              style={{ fontSize: "11px" }}
                            >
                              View Invoice →
                            </Link>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="text-center mt-3">
                  <Link
                    to={`/admin/orders/${orderId}`}
                    className="btn btn-sm btn-outline-primary w-100 rounded-pill fw-semibold d-inline-flex align-items-center justify-content-center gap-1"
                  >
                    Open in Order Manager <FaExternalLinkAlt size={10} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
